import { useEffect, useState } from 'react';
import { BellRing, X } from 'lucide-react';

import {
  detectPushSupport,
  ensurePushReadyForDevice,
  fetchPushConfig,
  getNotificationPermissionSafely,
} from '../utils/pushNotifications';
import { syncUnreadAppBadge } from '../utils/appBadge';

const AUTO_PROMPT_SESSION_KEY = 'customer_push_prompt_seen_in_session_v1';
const AUTO_PROMPT_DELAY_MS = 450;

const LoginNotificationPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canEnable, setCanEnable] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const nextSupported = detectPushSupport();
    setSupported(nextSupported);

    if (!nextSupported || getNotificationPermissionSafely() !== 'default') {
      return;
    }

    let cancelled = false;

    const prepare = async () => {
      try {
        const config = await fetchPushConfig();
        if (cancelled) return;

        const available = Boolean(config?.enabled && config.public_key);
        setCanEnable(available);
        if (!available) return;

        try {
          if (window.sessionStorage.getItem(AUTO_PROMPT_SESSION_KEY) === '1') {
            return;
          }
        } catch {
          // If session storage is unavailable, show the prompt once anyway.
        }

        const timer = window.setTimeout(() => {
          if (!cancelled) {
            setShowPrompt(true);
          }
        }, AUTO_PROMPT_DELAY_MS);

        return () => {
          window.clearTimeout(timer);
        };
      } catch (error) {
        console.error(error);
      }

      return undefined;
    };

    let cleanup: (() => void) | undefined;
    void prepare().then((dispose) => {
      cleanup = dispose;
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  const dismiss = () => {
    setShowPrompt(false);
    try {
      window.sessionStorage.setItem(AUTO_PROMPT_SESSION_KEY, '1');
    } catch {
      // Ignore storage errors.
    }
  };

  const handleEnable = async () => {
    setLoading(true);

    try {
      const enabled = await ensurePushReadyForDevice();
      if (enabled || getNotificationPermissionSafely() !== 'default') {
        if (enabled) {
          void syncUnreadAppBadge();
        }
        dismiss();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!supported || !canEnable || !showPrompt) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/45 p-3 sm:items-center">
      <div className="w-full max-w-md overflow-hidden rounded-[30px] bg-white shadow-[0_28px_80px_rgba(15,23,42,0.28)]">
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 px-5 py-4 text-white">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70">
                Message alerts
              </div>
              <h3 className="mt-1 text-xl font-semibold">Turn on notifications for this device</h3>
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"
              aria-label="Close notification prompt"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          <p className="text-sm leading-6 text-slate-600">
            Get booking reminders and store messages without needing to keep this screen open.
          </p>

          <div className="rounded-[24px] border border-emerald-200 bg-emerald-50/80 p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-2.5 text-emerald-700">
                <BellRing className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900">It only takes a moment</div>
                <div className="mt-1 text-sm leading-6 text-slate-600">
                  Tap the button below, then tap <span className="font-semibold text-slate-900">Allow</span> in the next popup.
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              void handleEnable();
            }}
            disabled={loading}
            className="flex min-h-[52px] w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Turning on...' : 'Turn on notifications'}
          </button>

          <button
            type="button"
            onClick={dismiss}
            className="flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginNotificationPrompt;
